/**
 * RecursionSchemesExplainer — progressive-disclosure explainer for recursion schemes.
 *
 * Recursion schemes are the concrete program-level consequences of having an
 * initial F-algebra and terminal F-coalgebra. They are canonical operators
 * characterized by universal properties, not ad hoc traversals.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import { FONT, GRAY, PRIMARY, THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  HylomorphismDiagram,
  InitialAlgebraDiagram,
  TerminalCoalgebraDiagram,
  usePulsePhase,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import treeNodes from "./recursion-tree-nodes.data.json" with { type: "json" };

const ACCENT = PRIMARY[700];
const COALGEBRA_ACCENT = THEOREM_COLOR["INV.PRESERVE"];
const ALGEBRA_ACCENT = THEOREM_COLOR["INV.ESTABLISH"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-recursion-schemes",
  kind: "Categorical structure",
  title: "Recursion schemes",
  subtitle: "cata · ana · hylo · para · apo · zygo",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "F-Algebra", href: "#explainer-falgebra", accent: ALGEBRA_ACCENT },
  { label: "F-Coalgebra", href: "#explainer-fcoalgebra", accent: COALGEBRA_ACCENT },
  { label: "Bialgebra", href: "#explainer-bialgebra" },
  { label: "INV.ESTABLISH (I1)", href: "#explainer-inv-establish", accent: ALGEBRA_ACCENT },
  { label: "INV.PRESERVE (I2)", href: "#explainer-inv-preserve", accent: COALGEBRA_ACCENT },
];

type SchemeDirection = "up" | "down" | "both";
const ANIMATION_PERIOD_MS = 1200;
interface TreeNode {
  readonly id: string;
  readonly cx: number;
  readonly cy: number;
  readonly level: number;
  readonly label: string;
}

const TREE_NODES = treeNodes as readonly TreeNode[];
const FUSION_NODE_X = 130;
const FUSION_NODE_Y = 116;
const FUSION_NODE_RADIUS = 13;
const FUSION_NODE_ALPHA_DIMMED = "0D";
const FUSION_NODE_ALPHA_VISIBLE = "40";

function SchemeTree({
  title,
  combinator,
  direction,
  accent,
  note,
  showSubstructure = false,
  showEarlyExit = false,
}: {
  readonly title: string;
  readonly combinator: string;
  readonly direction: SchemeDirection;
  readonly accent: string;
  readonly note: string;
  readonly showSubstructure?: boolean;
  readonly showEarlyExit?: boolean;
}): React.JSX.Element {
  const markerId = useId();
  const phase = usePulsePhase(4, ANIMATION_PERIOD_MS);
  let activeLevel: number;
  switch (direction) {
    case "down":
      activeLevel = Math.min(phase, 2);
      break;
    case "up":
      activeLevel = Math.max(0, 2 - phase);
      break;
    case "both":
      activeLevel = phase % 3;
      break;
  }
  const activeLevels =
    direction === "both"
      ? [phase % 3, 2 - (phase % 3)]
      : [activeLevel, ...(phase >= 3 ? [0, 1, 2] : [])];

  const nodeActive = (level: number): boolean => activeLevels.includes(level);
  const arrowTransform =
    direction === "down"
      ? `translateY(${Math.min(phase, 2) * 17}px)`
      : direction === "up"
        ? `translateY(${-Math.min(phase, 2) * 17}px)`
        : `translateY(${phase % 2 === 0 ? -8 : 8}px)`;

  return (
    <figure style={{ margin: 0 }}>
      <figcaption
        style={{
          fontFamily: FONT.body,
          fontSize: "0.8125rem",
          color: GRAY[900],
          marginBottom: "0.25rem",
          fontWeight: 600,
        }}
      >
        {title} (<code>{combinator}</code>)
      </figcaption>
      <svg
        viewBox="0 0 210 160"
        role="img"
        aria-label={`${title} tree animation`}
        style={{ display: "block" }}
      >
        <defs>
          <marker
            id={`${markerId}-head`}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M0 0 L10 5 L0 10z" fill={accent} />
          </marker>
        </defs>
        <path
          d="M105 26 L72 66 M105 26 L138 66 M72 66 L47 108 M72 66 L92 108 M138 66 L118 108 M138 66 L163 108"
          stroke={GRAY[400]}
          strokeWidth="1.5"
          fill="none"
        />
        {TREE_NODES.map((node) => (
          <g key={node.id}>
            <circle
              cx={node.cx}
              cy={node.cy}
              r={6}
              fill={nodeActive(node.level) ? accent : withAlpha(GRAY[500], "44")}
              stroke={nodeActive(node.level) ? accent : GRAY[500]}
              style={{ transition: "fill 320ms ease, stroke 320ms ease, opacity 320ms ease" }}
            />
            {node.label === "root" && (
              <text x={node.cx} y={node.cy - 12} textAnchor="middle" fontSize="10" fill={GRAY[700]}>
                μF
              </text>
            )}
          </g>
        ))}
        <path
          d={direction === "down" ? "M105 20 L105 100" : "M105 110 L105 30"}
          stroke={accent}
          strokeWidth="2"
          fill="none"
          markerEnd={`url(#${markerId}-head)`}
          style={{
            transform: arrowTransform,
            transition: "transform 450ms ease, opacity 320ms ease",
          }}
        />
        {showSubstructure && (
          <g style={{ opacity: phase >= 1 ? 1 : 0.35, transition: "opacity 320ms ease" }}>
            <path d="M35 121 L97 121" stroke={ACCENT} strokeDasharray="4 3" />
            <text x="66" y="135" textAnchor="middle" fontSize="10" fill={GRAY[700]}>
              sees tail
            </text>
          </g>
        )}
        {showEarlyExit && (
          <g style={{ opacity: phase >= 1 ? 1 : 0.3, transition: "opacity 320ms ease" }}>
            <circle cx="163" cy="108" r="10" fill={withAlpha(ACCENT, "1A")} stroke={ACCENT} />
            <text x="163" y="112" textAnchor="middle" fontSize="8" fill={ACCENT}>
              exit
            </text>
          </g>
        )}
      </svg>
      <p style={{ margin: "0.2rem 0 0", fontSize: "0.75rem", color: GRAY[600], maxWidth: "28ch" }}>
        {note}
      </p>
    </figure>
  );
}

function HylomorphismFusionTree(): React.JSX.Element {
  const phase = usePulsePhase(4, ANIMATION_PERIOD_MS);
  return (
    <figure style={{ margin: 0 }}>
      <figcaption
        style={{
          fontFamily: FONT.body,
          fontSize: "0.8125rem",
          color: GRAY[900],
          marginBottom: "0.25rem",
          fontWeight: 600,
        }}
      >
        Hylomorphism (<code>hylo</code>)
      </figcaption>
      <svg
        viewBox="0 0 260 160"
        role="img"
        aria-label="Hylomorphism fusion law: unfold-step and fold-step in one recursion"
      >
        <path
          d="M40 88 L68 58 L96 88 M96 88 L120 116 M164 116 L188 88 M188 88 L216 58 L244 88"
          stroke={GRAY[400]}
          strokeWidth="1.5"
          fill="none"
        />
        <text x="68" y="48" textAnchor="middle" fontSize="10" fill={GRAY[700]}>
          β : A → F A
        </text>
        <text x="216" y="48" textAnchor="middle" fontSize="10" fill={GRAY[700]}>
          α : F B → B
        </text>
        <circle
          cx={FUSION_NODE_X}
          cy={FUSION_NODE_Y}
          r={FUSION_NODE_RADIUS}
          fill={withAlpha(
            ACCENT,
            phase >= 2 ? FUSION_NODE_ALPHA_DIMMED : FUSION_NODE_ALPHA_VISIBLE,
          )}
          stroke={ACCENT}
          style={{ transition: "opacity 320ms ease, fill 320ms ease" }}
        />
        <text
          x={FUSION_NODE_X}
          y="120"
          textAnchor="middle"
          fontSize="9"
          fill={GRAY[700]}
          style={{ opacity: phase >= 2 ? 0.3 : 1, transition: "opacity 320ms ease" }}
        >
          hylo
        </text>
        <path
          d="M74 66 L122 108"
          stroke={COALGEBRA_ACCENT}
          strokeWidth="2"
          fill="none"
          style={{ opacity: phase >= 1 ? 0.4 : 1, transition: "opacity 320ms ease" }}
        />
        <path
          d="M188 96 L138 112"
          stroke={ALGEBRA_ACCENT}
          strokeWidth="2"
          fill="none"
          style={{ opacity: phase >= 1 ? 0.4 : 1, transition: "opacity 320ms ease" }}
        />
        <path
          d="M56 136 L206 136"
          stroke={ACCENT}
          strokeWidth="2.5"
          fill="none"
          style={{ opacity: phase >= 2 ? 1 : 0.2, transition: "opacity 320ms ease" }}
        />
        <text
          x="130"
          y="151"
          textAnchor="middle"
          fontSize="10"
          fill={ACCENT}
          style={{ opacity: phase >= 2 ? 1 : 0.35, transition: "opacity 320ms ease" }}
        >
          hylo α β a ≡ α (fmap (hylo α β) (β a))
        </text>
      </svg>
      <p style={{ margin: "0.2rem 0 0", fontSize: "0.75rem", color: GRAY[600], maxWidth: "34ch" }}>
        The fusion law characterizes one recursive step directly, so unfold and fold stay coherent
        in a single pass.
      </p>
    </figure>
  );
}

function RecursionSchemeFamilyDiagram(): React.JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gap: "0.75rem",
        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
      }}
    >
      <SchemeTree
        title="Catamorphism"
        combinator="cata"
        direction="up"
        accent={ALGEBRA_ACCENT}
        note="Universal fold from μF into an algebra carrier."
      />
      <SchemeTree
        title="Anamorphism"
        combinator="ana"
        direction="down"
        accent={COALGEBRA_ACCENT}
        note="Universal unfold into νF from a coalgebra seed."
      />
      <HylomorphismFusionTree />
      <SchemeTree
        title="Paramorphism"
        combinator="para"
        direction="up"
        accent={ALGEBRA_ACCENT}
        note="Fold that can read each recursive substructure while combining."
        showSubstructure
      />
      <SchemeTree
        title="Apomorphism"
        combinator="apo"
        direction="down"
        accent={COALGEBRA_ACCENT}
        note="Unfold that can emit a final branch immediately (early exit)."
        showEarlyExit
      />
      <SchemeTree
        title="Zygomorphism"
        combinator="zygo"
        direction="both"
        accent={ACCENT}
        note="Mutual recursion packaged as one fold with paired algebras."
      />
    </div>
  );
}

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>Recursion schemes</strong> are the canonical recursion operators derived from initial
      algebras and terminal coalgebras. They give CSK provably correct folds and unfolds rather than
      ad hoc traversals.
    </p>
  ),
  intuition: (
    <>
      <p>
        An initial F-algebra gives a unique way to consume recursive structure (<code>cata</code>),
        and a terminal F-coalgebra gives a unique way to produce it (<code>ana</code>). Everything
        else in the family is a controlled composition: <code>hylo</code> fuses unfold+fold,{" "}
        <code>para</code> exposes substructure during folding, <code>apo</code> allows early exit
        during unfolding, and <code>zygo</code> computes mutually dependent folds together.
      </p>
      <p>
        This is exactly what makes "F-algebras are stronger than the initial state of a state
        machine" concrete: scheme laws guarantee recursion behavior by construction at the
        combinator level.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        For an endofunctor <MathInline>{tex`F`}</MathInline>, each scheme is uniquely characterized
        by a universal property:
      </p>
      <ul>
        <li>
          <code>cata</code> <MathInline>{tex`\alpha : \mu F \to A`}</MathInline> is the unique
          algebra morphism from the initial algebra (see the F-Algebra card for the universal square
          below):
        </li>
      </ul>
      <InitialAlgebraDiagram accent={ALGEBRA_ACCENT} targetAccent={GRAY[600]} />
      <ul style={{ marginTop: "0.5rem" }}>
        <li>
          <code>ana</code> <MathInline>{tex`\gamma : S \to \nu F`}</MathInline> is the unique
          coalgebra morphism into the terminal coalgebra (the dual picture):
        </li>
      </ul>
      <TerminalCoalgebraDiagram accent={COALGEBRA_ACCENT} sourceAccent={GRAY[600]} />
      <ul style={{ marginTop: "0.5rem" }}>
        <li>
          <code>hylo</code> fusion law{" "}
          <MathInline ariaLabel="hylo α γ equals α ∘ F(hylo α γ) ∘ γ">
            {tex`\mathrm{hylo}\, \alpha\, \gamma \;\equiv\; \alpha \circ F(\mathrm{hylo}\, \alpha\, \gamma) \circ \gamma`}
          </MathInline>{" "}
          is exposed by the hylomorphism witness record — a single pass that never materializes the
          intermediate <MathInline>{tex`\mu F`}</MathInline>:
        </li>
      </ul>
      <HylomorphismDiagram
        accent={ACCENT}
        algebraAccent={ALGEBRA_ACCENT}
        coalgebraAccent={COALGEBRA_ACCENT}
      />
      <ul style={{ marginTop: "0.5rem" }}>
        <li>
          <code>para</code>, <code>apo</code>, and <code>zygo</code> are enriched forms preserving
          those same universality constraints while exposing additional structure.
        </li>
      </ul>
      <p>
        The card header diagrams above show the directional recursion shape for each scheme and the
        hylo fusion equation in animated form. The original hylo law also surfaces as{" "}
        <MathInline ariaLabel="hylo α β a equals α applied to fmap (hylo α β) (β a)">
          {tex`\mathrm{hylo}\, \alpha\, \beta\, a \;\equiv\; \alpha\,(\mathrm{fmap}\,(\mathrm{hylo}\, \alpha\, \beta)\,(\beta\, a))`}
        </MathInline>{" "}
        — the same equation, written with the functor's <code>fmap</code> spelled out.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        CSK's constructive tactic surface is organized around these recursion schemes, because each
        one gives a reusable proof pattern with explicit termination and uniqueness semantics. The
        resulting tactic behavior is governed structurally, not by one-off traversal code.
      </p>
      <ul>
        <li>
          <strong>F-algebra side:</strong> <code>cata</code>, <code>para</code>, and{" "}
          <code>zygo</code> package canonical folds from <MathInline>{tex`\mu F`}</MathInline>.
        </li>
        <li>
          <strong>F-coalgebra side:</strong> <code>ana</code> and <code>apo</code> package canonical
          unfolds into <MathInline>{tex`\nu F`}</MathInline>, including early-exit branches.
        </li>
        <li>
          <strong>Fusion:</strong> <code>hylo</code> captures the algebra-coalgebra composition in a
          single pass.
        </li>
      </ul>
    </>
  ),
  references: [
    {
      label: "Recursion-Scheme-Analysis-Of-Constructive-Tactics",
      path: "theory/algebra/Recursion-Scheme-Analysis-Of-Constructive-Tactics.md",
      note: "Canonical CSK analysis of cata/ana/hylo/para/apo/zygo and tactic coverage.",
    },
    {
      label: "Recursion schemes (Agda)",
      path: "agda/MathLibCore/Lemmas/RecursionSchemes.agda",
      note: "Mechanized generic recursion-scheme records and laws.",
    },
    {
      label: "F-Algebra explainer",
      path: "demos/ui/src/explainers/categorical/FAlgebraExplainer.tsx",
    },
    {
      label: "F-Coalgebra explainer",
      path: "demos/ui/src/explainers/categorical/FCoalgebraExplainer.tsx",
    },
  ],
};

export function RecursionSchemesExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<RecursionSchemeFamilyDiagram />}
      crossLinks={CROSS_LINKS}
    />
  );
}
